import { keepPreviousData, useQuery, type UseQueryResult } from "@tanstack/react-query";
import type { components } from "@oathera/contract/api";
import { apiFetch, type ApiError } from "../../lib/api.ts";
import { useTimeRange } from "../../state/time-range.tsx";

/**
 * The reads for the Traces screen (task 11.11; design 11, 12.2 Traces row).
 *
 * TWO operator-API operations back the screen, both role `viewer`, taken straight from the generated
 * `@oathera/contract/api` and never redefined here:
 *
 * - `GET /api/v1/traces?from=…&to=…` → {@link TracePage}: one page of {@link TraceSummary} rows. Each
 *   summary carries, per contract, `trace_id`, `root_span_name`, `services` (the services the trace
 *   touched), `started_at`, `duration_ms`, `worst_status`, `span_count`, AND — the heart of this
 *   screen — the TWO INDEPENDENT booleans `inferred` and `partial`. The contract itself states they
 *   are independent: both may be true and neither takes precedence (FR-8.2 / FR-8.3). The list reads
 *   those two flags straight off each row; nothing collapses or orders them.
 * - `GET /api/v1/traces/{id}` → {@link TraceDetail}: the chosen trace's full waterfall — it EXTENDS
 *   `TraceSummary` (so the detail carries the same trace-level `inferred`/`partial`) and adds `spans`
 *   (one {@link Span} each — span_id, parent_span_id, service, name, kind, start_ts, duration_ms,
 *   status), a `correlation` object (how an inferred trace was correlated, or `null`), and `meta`.
 *   The span waterfall is drawn from `spans`.
 *
 * The LIST is a WINDOWED read (it takes `from`/`to`), so — exactly like the Overview/Decisions/…
 * screens — the window is resolved from the shared time-range and pausing freezes the query: in live
 * mode "now" is bucketed to a 10s grid to keep the query key stable between ticks; when frozen
 * (paused or custom) the exact frozen window is used and the key stops advancing (design 12.2,
 * FR-13 AC2). The DETAIL read is keyed only on the trace id (a trace is immutable once closed), so a
 * trace already drawn stays drawn when the operator pauses — the time-range governs the LIST that
 * surfaces traces, not the identity of a trace already opened.
 *
 * Fetches go through `lib/api.ts`, so a cold-core 503 surfaces as a `waking` {@link ApiError} and
 * the shell shows "Waking the control plane"; a 404 for an unknown trace id surfaces as `not_found`;
 * queries are retried per `query-client.ts`.
 */

/** The contract `TracePage` response body (one page of trace summaries). */
export type TracePage = components["schemas"]["TracePage"];
/** A single trace summary row (carries the independent `inferred`/`partial` flags). */
export type TraceSummary = components["schemas"]["TraceSummary"];
/** The contract `TraceDetail` response body (summary + spans + correlation + meta). */
export type TraceDetail = components["schemas"]["TraceDetail"];
/** A single span of a trace. */
export type Span = components["schemas"]["Span"];
/** The worst/own span status (`ok | error | denied`). */
export type SpanStatus = components["schemas"]["SpanStatus"];

/** The resolved time window a query ran for, so the screen can label the snapshot honestly. */
export interface TracesWindow {
  readonly fromMs: number;
  readonly toMs: number;
}

export interface UseTracesResult {
  readonly query: UseQueryResult<TracePage, ApiError>;
  /** The window the current query key was built from (what the list describes). */
  readonly window: TracesWindow;
}

export interface UseTraceResult {
  readonly query: UseQueryResult<TraceDetail, ApiError>;
}

/**
 * Fetch one page of trace summaries for the current window. The query key carries the window so a
 * frozen window never refetches and a live window advances on the 10s grid; `staleTime` /
 * `refetchInterval` match the other windowed screens (live tolerates a little staleness, frozen
 * never goes stale). The first page only — `next_cursor` is left for a later pagination affordance,
 * matching the identity/posture screens. The `inferred`/`partial` query filters the contract offers
 * are intentionally NOT set here: the screen shows ALL traces with their flags intact so an operator
 * can see every combination (none / inferred / partial / both) side by side.
 */
export function useTraces(): UseTracesResult {
  const timeRange = useTimeRange();
  const nowMs = timeRange.live ? bucket(Date.now(), 10_000) : Date.now();
  const resolved = timeRange.resolveWindow(nowMs);
  const window: TracesWindow = { fromMs: resolved.fromMs, toMs: resolved.toMs };

  const query = useQuery<TracePage, ApiError>({
    queryKey: ["traces", window.fromMs, window.toMs],
    queryFn: ({ signal }) =>
      apiFetch<TracePage>("/traces", {
        query: { from: window.fromMs, to: window.toMs },
        signal,
      }),
    staleTime: timeRange.live ? 10_000 : Infinity,
    refetchInterval: timeRange.live ? 15_000 : false,
    // Keep the previous window's data while the next live window fetches, so the screen does
    // not blink through its loading state on each ~10s tick (seamless live refresh).
    placeholderData: keepPreviousData,
  });

  return { query, window };
}

/**
 * Fetch one trace's full detail (spans + correlation) by id. A trace is immutable once closed, so
 * the query is keyed on the id alone and never goes stale — opening a trace does not depend on the
 * live/pause state, and the detail does not poll. `enabled` is false when there is no id (e.g. an
 * invalid route param), so no request goes out for a trace that cannot exist; a placeholder id keeps
 * the key well-typed while disabled.
 */
export function useTrace(id: string | undefined, options: { enabled?: boolean } = {}): UseTraceResult {
  const enabled = (options.enabled ?? true) && id !== undefined && id.length > 0;
  const traceId = id ?? "";

  const query = useQuery<TraceDetail, ApiError>({
    queryKey: ["trace", traceId],
    queryFn: ({ signal }) =>
      apiFetch<TraceDetail>(`/traces/${encodeURIComponent(traceId)}`, { signal }),
    enabled,
    staleTime: Infinity,
    refetchInterval: false,
  });

  return { query };
}

/** Floor `value` to the nearest multiple of `size` (keeps the live query key stable). */
function bucket(value: number, size: number): number {
  return Math.floor(value / size) * size;
}
